// main imports
import React, { Component } from "react";
import { connect } from "react-redux";
import { match } from "react-router-dom";
import { History, Location } from "history";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";

// backend
import { getBackend } from "@/lib/backend/backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { waitForManufacturingAsync, getPlantsForUser } from "@/lib/businessLogic";

// import subcomponents
import StockLocationsList from "./StockLocationsList";
import StockLocationsDetails from "./StockLocationsDetails";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as Redirect from "@/app/URLHandler/redirect";
import { ResourcesLayout } from "@/modules/shared/MainContent";

import type { ResourceEditFiltersState } from "@/store/reducers/resourceEditFiltersReducer";
import type { ReduxState } from "@/store/reducers";

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
    resource_edit_filters: ResourceEditFiltersState;
};

type State = {
    items: t.IStockLocation[];
    materials: t.IStockLocationMaterial[];
    plants: t.IPlantData[];
    plant_uuid: string;
    error: string;
    warning: string;
    selected: t.IStockLocation | null;
    title_filter: string;
    plant_filter: string;
    searchValue: string;
};

const createNavigationUrl = (uuid: string = "") => {
    return `/digital-twin/resources/stock_locations/${uuid}`;
};

/**
 * Displaying production plants list.
 */
class StockLocations extends Component<Props, State> {
    itemsListRef: { current: null | StockLocationsList };

    constructor(props: Props) {
        super(props);
        this.state = {
            items: [],
            materials: [],
            plants: [],
            plant_uuid: "",
            error: "",
            warning: "",
            selected: null,
            title_filter: "",
            plant_filter: "",
            searchValue: "",
        };
        this.itemsListRef = React.createRef();
    }

    async loadComponent() {
        try {
            await waitForManufacturingAsync();
            const plants = getPlantsForUser();
            const plant_uuids = plants.map(x => x.uuid);
            const res = await getBackend().manufacturing.getStockLocations({});
            const items = res.locations
                .filter(x => plant_uuids.indexOf(x.plant_uuid) >= 0)
                .sort((x1, x2) => x1.external_id.localeCompare(x2.external_id));

            let selected: t.IStockLocation | null = null;
            let materials: t.IStockLocationMaterial[] = [];

            // Select initial plant.
            if (this.props.match.params.uuid) {
                const filtered = items.filter(item => item.uuid === this.props.match.params.uuid);
                if (filtered.length) {
                    selected = filtered[0];
                }
            } else if (this.props.resource_edit_filters.stock_locations_title_filter) {
                const filter = this.props.resource_edit_filters.stock_locations_title_filter.toLowerCase();
                const filtered = items.filter(
                    item =>
                        item.title.toLowerCase().includes(filter) ||
                        item.uuid.toLowerCase().includes(filter) ||
                        item.external_id.toLowerCase().includes(filter)
                );
                if (filtered.length) {
                    selected = filtered[0];
                }
            } else if (items.length) {
                selected = items[0];
            }

            if (selected) {
                materials = (await getBackend().manufacturing.getStockLocationMaterials({ uuid: selected.uuid }))
                    .materials;
            }

            this.setState({
                items,
                materials,
                plants,
                selected,
                error: "",
                warning: "",
            });

            this.props.history.push(createNavigationUrl(selected?.uuid));
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    componentDidMount() {
        // Initialize search value from Redux state
        this.setState({
            searchValue: this.props.resource_edit_filters.stock_locations_title_filter || "",
        });
        this.loadComponent();
    }

    async componentDidUpdate(prev_props: Props) {
        if (
            prev_props.resource_edit_filters.plant_uuid !== this.props.resource_edit_filters.plant_uuid ||
            prev_props.resource_edit_filters.stock_locations_title_filter !==
                this.props.resource_edit_filters.stock_locations_title_filter
        ) {
            await this.loadComponent();
        }

        // Sync local search value with Redux state when it changes externally
        const current_filter = this.props.resource_edit_filters.stock_locations_title_filter || "";
        const prev_filter = prev_props.resource_edit_filters.stock_locations_title_filter || "";
        if (current_filter !== prev_filter && current_filter !== this.state.searchValue) {
            this.setState({ searchValue: current_filter });
        }
    }

    /**
     * Select the first line that matches all the given criteria.
     * @param {string} contentFilter Line search term.
     * @param {string} plant_uuid Plant UUID.
     * @param {t.IStockLocation[]} [items] List of items to select from.
     */
    selectItem(
        contentFilter: string,
        plant_uuid: string,
        items: t.IStockLocation[] = this.state.items
    ): t.IStockLocation | null {
        for (let item of items) {
            const visible = this.itemsListRef.current && this.itemsListRef.current.includes(contentFilter, item);

            if ((plant_uuid === item.plant_uuid || plant_uuid === "") && visible) {
                return item;
            }
        }

        return null;
    }

    /**
     * Handling change in search input - uses local state to prevent cursor jumping
     */
    handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const value = event.currentTarget.value;
            const selected = this.selectItem(value, this.props.resource_edit_filters.plant_uuid);

            this.setState({
                searchValue: value,
                selected,
            });

            Redirect.resourceEditFilters(this.props.history, createNavigationUrl(selected ? selected.uuid : ""), {
                ...this.props.resource_edit_filters,
                stock_locations_title_filter: value,
            });
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    };

    /**
     * Handling change in any input component (non-search inputs).
     */
    handleInputChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        try {
            // read new values
            const target = event.currentTarget;
            const value = target.value;
            const name = target.name;

            if (name === "plant_uuid") {
                const new_plant_uuid = value;
                const selected = this.selectItem(this.state.searchValue, new_plant_uuid);

                Redirect.resourceEditFilters(this.props.history, createNavigationUrl(selected ? selected.uuid : ""), {
                    ...this.props.resource_edit_filters,
                    plant_uuid: new_plant_uuid,
                });

                let materials: t.IStockLocationMaterial[] = [];
                if (selected !== null) {
                    materials = (await getBackend().manufacturing.getStockLocationMaterials({ uuid: selected.uuid }))
                        .materials;
                }

                this.setState({
                    selected,
                    materials,
                });
            }
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    };

    /**
     * Handle `click` events for plant items.
     */
    async handleItemClick(item: t.IStockLocation) {
        try {
            // load materials
            const { materials } = await getBackend().manufacturing.getStockLocationMaterials({ uuid: item.uuid });
            this.setState({
                materials,
                selected: item,
            });

            // Update URL.
            this.props.history.push(createNavigationUrl(item.uuid));
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    }

    /**
     * Handling error from subordinate component.
     */
    handleToError = (str: string) => {
        this.setState({ error: str });
    };

    render() {
        return (
            <ResourcesLayout>
                <ResourcesLayout.Main>
                    <ResourcesLayout.List>
                        <ResourcesLayout.Search>
                            <div className="form-group form-group-stretch">
                                <input
                                    type="text"
                                    className="form-control search_bar"
                                    placeholder={translate("common.search", "Search") + " ..."}
                                    name="title_filter"
                                    value={this.state.searchValue}
                                    onChange={this.handleSearchInputChange}
                                />
                            </div>
                            <div className="form-group">
                                <select
                                    name="plant_uuid"
                                    className="form-control select_filter_control"
                                    value={this.props.resource_edit_filters.plant_uuid}
                                    onChange={this.handleInputChange}
                                >
                                    <option key={-1} value="">
                                        {translate("Manufacturing.Lines.all_plants", "All Plants")}
                                    </option>
                                    {this.state.plants.map((plant, i) => {
                                        return (
                                            <option key={i} value={plant.uuid}>
                                                {plant.title}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        </ResourcesLayout.Search>
                        <StockLocationsList
                            ref={this.itemsListRef}
                            items={this.state.items}
                            selected={this.state.selected}
                            filter={this.state.searchValue}
                            plantFilter={this.props.resource_edit_filters.plant_uuid}
                            handleItemClick={item => {
                                this.handleItemClick(item);
                            }}
                        />
                    </ResourcesLayout.List>
                    <ResourcesLayout.Content>
                        <ErrorComponent msg={this.state.error} type="error" />
                        <ErrorComponent msg={this.state.warning} type="warning" />
                        {this.state.items.length > 0 && this.state.selected && (
                            <StockLocationsDetails
                                plantFilter={this.state.selected ? this.state.selected.uuid : ""}
                                selected={this.state.selected}
                                materials={this.state.materials}
                            />
                        )}
                    </ResourcesLayout.Content>
                </ResourcesLayout.Main>
            </ResourcesLayout>
        );
    }
}

export default connect((state: ReduxState) => {
    return { resource_edit_filters: state.resource_edit_filters };
}, null)(StockLocations);
